<template>
  <layout-report-step
    :isDone="isDone"
    :title="header.title"
    :description="header.description"
    :image="header.image"
    :debug="header.debug"
  >
    <v-card class="pt-4" flat max-width="500">
      <v-radio-group v-model="reportType">
        <v-row>
          <v-col cols="6" md="4" v-for="(item, index) in activeReportTypeConfigurations" :key="index">
            <v-card
              flat
              outlined
              class="mx-1 my-2 report-type-card"
              :class="{ 'report-type-card--selected': reportType === item.type }"
              :style="reportType === item.type ? selectedCardStyle : undefined"
              height="120"
              @click="reportType = item.type"
            >
              <v-radio class="mx-2 mt-2" :label="item.label" :value="item.type" :color="color"></v-radio>
              <v-img contain :src="item.image" max-height="70" />
            </v-card>
          </v-col>
        </v-row>
      </v-radio-group>
    </v-card>
  </layout-report-step>
</template>

<script lang="ts">
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { AssetEnum, AssetRepository } from "@/lib/AssetRepository";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IReportHeaderData } from "@/lib/interfaces/Report/IReportHeaderData";
import { IReportStep } from "@/lib/interfaces/Report/IViewReportStep";
import ReportStepMixin from "@/mixins/ReportStepMixin.vue";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { ReportType } from "@/store/enum/reportType";
import { ReportModule } from "@/store/modules/report.store";
import { Component } from "vue-property-decorator";

@Component({
  components: {
    LayoutReportStep
  }
})
export default class ReportTypeSelector extends ReportStepMixin implements IReportStep {
  header: IReportHeaderData = {
    title: "report.reportTypeSelector.title",
    description: "report.reportTypeSelector.description"
  };

  screen = ReportScreenEnum.reportType;

  AssetEnum = AssetEnum;

  car = ReportType.CAR;
  container = ReportType.CONTAINER;

  reportTypeConfiguration = [
    {
      type: ReportType.CAR,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.carFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.CAR}`
      )
    },
    {
      type: ReportType.CONTAINER,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.containerFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.CONTAINER}`
      )
    },
    {
      type: ReportType.MOTORBIKE,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.motorbikeFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.MOTORBIKE}`
      )
    },
    {
      type: ReportType.CRANE,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.craneFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.CRANE}`
      )
    },
    {
      type: ReportType.TRUCK,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.truckFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.TRUCK}`
      )
    },
    {
      type: ReportType.TRACTOR_UNIT,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.tractorUnitFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.TRACTOR_UNIT}`
      )
    },
    {
      type: ReportType.TRAILER,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.trailerFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.TRAILER}`
      )
    },
    {
      type: ReportType.TRUCK_TRAILER,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.truckTrailerFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.TRUCK_TRAILER}`
      )
    },
    {
      type: ReportType.SEMITRAILER,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.semitrailerFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.SEMITRAILER}`
      )
    },
    {
      type: ReportType.BICYCLE,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.bicycleFrontLeft),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.BICYCLE}`
      )
    },
    {
      type: ReportType.OTHER,
      image: AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.emptyData),
      label: this.$t(
        `components.partner.PartnerReportDetail.PartnerReportUpdateReportDialog.report.reportTypes.${ReportType.OTHER}`
      )
    }
  ];

  get activeReportTypeConfigurations() {
    return this.reportTypeConfiguration.filter((item) => ReportModule.activeReportTypes.includes(item.type));
  }

  get reportType() {
    return ReportModule.reportType;
  }

  set reportType(value: ReportType) {
    ReportModule.setReportType(value);
    // Clear selected damages on other type of vehicle.
    // When switching between car and truck don't store specific parts of the truck in the car damages
    ReportModule.clearDamageLocation();
  }

  get isDone() {
    return true;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get selectedCardStyle() {
    const hex = this.color.slice(0, 7);
    return {
      borderColor: hex,
      borderWidth: "2px",
      backgroundColor: hex + "1A"
    };
  }
}
</script>

<style scoped>
.center {
  text-align: center;
}

.report-type-card--selected >>> .v-label {
  font-weight: bold;
}

.report-type-card {
  transition:
    border-color 0.2s,
    background-color 0.2s;
  cursor: pointer;
}
</style>
